/**
 * Transaction Queue System
 * 
 * Handles concurrent blockchain transactions by queuing them and processing
 * them sequentially or with controlled concurrency to prevent:
 * - Race conditions
 * - Nonce conflicts
 * - Transaction failures due to simultaneous submissions
 * - Network congestion
 */

class TransactionQueue {
  constructor(maxConcurrency = 5) {
    this.queue = [];
    this.processing = new Set();
    this.maxConcurrency = maxConcurrency; // Increased from 3 to 5 for better throughput with large batches
    this.listeners = new Map();
    this.history = [];
    this.maxHistorySize = 100;
    
    // Track operation types to enforce per-type concurrency limits
    this.operationCounts = new Map(); // operationType -> count of active operations
  }

  /**
   * Add transaction to queue
   * @param {Function} transactionFn - Async function that executes the transaction
   * @param {Object} options - Transaction options
   * @returns {Promise} Resolves when transaction completes
   */
  async enqueue(transactionFn, options = {}) {
    const {
      priority = 0, // Higher priority = processed first
      timeout = 300000, // 5 minutes default timeout (increased for large batches)
      retries = 2,
      id = this.generateId(),
      onProgress = null,
      operationType = 'default', // NEW: Type of operation for per-type concurrency control
      maxConcurrencyForType = null // NEW: Max concurrency for this operation type (overrides global)
    } = options;

    return new Promise((resolve, reject) => {
      const task = {
        id,
        fn: transactionFn,
        resolve,
        reject,
        priority,
        timeout,
        retries,
        retriesLeft: retries,
        onProgress,
        operationType,
        maxConcurrencyForType,
        enqueuedAt: Date.now(),
        status: 'queued'
      };

      // Add to queue (maintain priority order)
      this.queue.push(task);
      this.queue.sort((a, b) => b.priority - a.priority);

      console.log(`📝 Transaction queued: ${id} [${operationType}] (${this.queue.length} in queue)`);

      // Start processing
      this.processNext();
    });
  }

  /**
   * Process next transaction(s) in queue
   */
  async processNext() {
    // Don't exceed max concurrency
    if (this.processing.size >= this.maxConcurrency) {
      return;
    }

    // Get next task from queue that doesn't exceed its type's concurrency limit
    let taskIndex = -1;
    let task = null;
    
    for (let i = 0; i < this.queue.length; i++) {
      const candidateTask = this.queue[i];
      
      // Check if this operation type has a specific concurrency limit
      const maxForType = candidateTask.maxConcurrencyForType;
      if (maxForType !== null) {
        const currentCount = this.operationCounts.get(candidateTask.operationType) || 0;
        
        // Skip this task if it would exceed its type's limit
        if (currentCount >= maxForType) {
          console.log(`⏸️ Skipping ${candidateTask.id} [${candidateTask.operationType}]: at max concurrency (${currentCount}/${maxForType})`);
          continue; // Try next task
        }
      }
      
      // This task can be processed
      taskIndex = i;
      task = candidateTask;
      break;
    }
    
    // No tasks can be processed right now
    if (!task) {
      return;
    }
    
    // Remove task from queue
    this.queue.splice(taskIndex, 1);

    // Mark as processing
    this.processing.add(task.id);
    task.status = 'processing';
    task.startedAt = Date.now();
    
    // Increment operation type counter
    const currentCount = this.operationCounts.get(task.operationType) || 0;
    this.operationCounts.set(task.operationType, currentCount + 1);

    console.log(`⚙️ Processing transaction: ${task.id} [${task.operationType}] (${this.processing.size} total, ${currentCount + 1} of type)`);

    // Notify progress
    if (task.onProgress) {
      task.onProgress({ status: 'processing', id: task.id });
    }

    // Execute with timeout
    try {
      const result = await this.executeWithTimeout(task);
      
      // Success
      task.status = 'completed';
      task.completedAt = Date.now();
      this.addToHistory(task);
      task.resolve(result);

      console.log(`✅ Transaction completed: ${task.id} (${Date.now() - task.enqueuedAt}ms total)`);
      
      if (task.onProgress) {
        task.onProgress({ status: 'completed', id: task.id, result });
      }
      
      // Decrement operation type counter
      const currentCount = this.operationCounts.get(task.operationType) || 0;
      this.operationCounts.set(task.operationType, Math.max(0, currentCount - 1));
    } catch (error) {
      // Check if we should retry
      if (task.retriesLeft > 0 && this.isRetriableError(error)) {
        task.retriesLeft--;
        console.warn(`🔄 Retrying transaction: ${task.id} (${task.retriesLeft} retries left)`);
        
        if (task.onProgress) {
          task.onProgress({ 
            status: 'retrying', 
            id: task.id, 
            retriesLeft: task.retriesLeft,
            error: error.message 
          });
        }

        // Re-queue with exponential backoff
        const backoffDelay = (task.retries - task.retriesLeft) * 1000;
        
        // Decrement operation type counter (will be incremented when re-queued task is processed)
        const retryCount = this.operationCounts.get(task.operationType) || 0;
        this.operationCounts.set(task.operationType, Math.max(0, retryCount - 1));
        
        setTimeout(() => {
          this.queue.unshift(task); // Add to front of queue
          this.processNext();
        }, backoffDelay);
      } else {
        // Failed permanently
        task.status = 'failed';
        task.error = error.message;
        task.completedAt = Date.now();
        this.addToHistory(task);
        task.reject(error);

        console.error(`❌ Transaction failed: ${task.id}`, error.message);
        
        if (task.onProgress) {
          task.onProgress({ status: 'failed', id: task.id, error: error.message });
        }
        
        // Decrement operation type counter
        const failCount = this.operationCounts.get(task.operationType) || 0;
        this.operationCounts.set(task.operationType, Math.max(0, failCount - 1));
      }
    } finally {
      // Remove from processing
      this.processing.delete(task.id);

      // Process next transaction(s)
      this.processNext();
      
      // If we can process more concurrently, do it
      if (this.queue.length > 0 && this.processing.size < this.maxConcurrency) {
        this.processNext();
      }
    }
  }

  /**
   * Execute transaction with timeout
   */
  async executeWithTimeout(task) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`Transaction timeout after ${task.timeout}ms`));
      }, task.timeout);

      task.fn()
        .then(result => {
          clearTimeout(timer);
          resolve(result);
        })
        .catch(error => {
          clearTimeout(timer);
          reject(error);
        });
    });
  }

  /**
   * Check if error is retriable
   */
  isRetriableError(error) {
    const retriableErrors = [
      'timeout',
      'network',
      'blockhash not found',
      'transaction not confirmed',
      'insufficient balance', // Can be temporary
      '429', // Rate limit
      '503', // Service unavailable
      'ECONNREFUSED',
      'ETIMEDOUT'
    ];

    const errorMsg = error.message?.toLowerCase() || '';
    return retriableErrors.some(keyword => errorMsg.includes(keyword));
  }

  /**
   * Generate unique transaction ID
   */
  generateId() {
    return `tx_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * Add to history for debugging/monitoring
   */
  addToHistory(task) {
    this.history.unshift({
      id: task.id,
      status: task.status,
      enqueuedAt: task.enqueuedAt,
      startedAt: task.startedAt,
      completedAt: task.completedAt,
      duration: task.completedAt - task.enqueuedAt,
      processingTime: task.completedAt - task.startedAt,
      error: task.error
    });

    // Keep history size manageable
    if (this.history.length > this.maxHistorySize) {
      this.history = this.history.slice(0, this.maxHistorySize);
    }
  }

  /**
   * Get queue status
   */
  getStatus() {
    return {
      queueLength: this.queue.length,
      processing: this.processing.size,
      maxConcurrency: this.maxConcurrency,
      recentTransactions: this.history.slice(0, 10)
    };
  }

  /**
   * Clear queue (emergency stop)
   */
  clearQueue() {
    const cleared = this.queue.length;
    this.queue.forEach(task => {
      task.reject(new Error('Queue cleared'));
    });
    this.queue = [];
    console.warn(`⚠️ Cleared ${cleared} transactions from queue`);
    return cleared;
  }

  /**
   * Get metrics for monitoring
   */
  getMetrics() {
    const completed = this.history.filter(t => t.status === 'completed');
    const failed = this.history.filter(t => t.status === 'failed');
    
    const avgDuration = completed.length > 0
      ? completed.reduce((sum, t) => sum + t.duration, 0) / completed.length
      : 0;

    return {
      queueLength: this.queue.length,
      processing: this.processing.size,
      totalCompleted: completed.length,
      totalFailed: failed.length,
      successRate: this.history.length > 0 
        ? (completed.length / this.history.length) * 100 
        : 100,
      avgDuration: Math.round(avgDuration),
      lastError: failed[0]?.error || null
    };
  }
}

// Create singleton instance
const transactionQueue = new TransactionQueue(3); // Max 3 concurrent transactions

export default transactionQueue;

// Export class for testing/custom instances
export { TransactionQueue };

/**
 * Helper function to wrap any transaction
 */
export async function queueTransaction(transactionFn, options = {}) {
  return transactionQueue.enqueue(transactionFn, options);
}

/**
 * Get queue metrics
 */
export function getTransactionMetrics() {
  return transactionQueue.getMetrics();
}

/**
 * Get queue status
 */
export function getTransactionStatus() {
  return transactionQueue.getStatus();
}

